import { supabase } from "../supabase";
import { isGenericConsumidorFinalClient } from "./consumidorFinal";
import {
  SALE_PAYMENT_TERMS_SELECT_FIELDS,
  getSalePaymentTermsColumnsSupport,
  isMissingSalePaymentTermsColumnsError,
  normalizeSalePaymentTermsRow,
  setSalePaymentTermsColumnsSupport,
} from "./salesPaymentTermsSupport";
import {
  attachStoreOrdersToSales,
  getDeliveredSaleStoreOrder,
  getSaleStoreOrders,
  isSaleAwaitingWebFulfillment,
  SALE_STORE_ORDER_SELECT,
} from "./saleStoreOrders";

// Embed Postgrest para traer los store_orders junto con cada venta en una sola
// query. Activa el fast-path en attachStoreOrdersToSales y evita el N+1 que
// detecta Sentry cuando hay muchas ventas (ref IMPULZA-3).
const SALES_WITH_STORE_ORDERS_EMBED = `store_orders!store_orders_sale_id_fkey(${SALE_STORE_ORDER_SELECT})`;

let clientCurrentBalanceColumnAvailable = null;
const CLIENT_CURRENT_BALANCE_SUPPORT_KEY = "impulza_clients_current_balance_support";

const readClientCurrentBalanceSupport = () => {
  if (clientCurrentBalanceColumnAvailable !== null) {
    return clientCurrentBalanceColumnAvailable;
  }

  if (typeof window === "undefined") {
    return null;
  }

  try {
    const rawValue = window.sessionStorage.getItem(CLIENT_CURRENT_BALANCE_SUPPORT_KEY);
    if (rawValue === "true") {
      clientCurrentBalanceColumnAvailable = true;
      return true;
    }
    if (rawValue === "false") {
      clientCurrentBalanceColumnAvailable = false;
      return false;
    }
  } catch {
    // Ignore storage access issues and continue with runtime detection.
  }

  return null;
};

const writeClientCurrentBalanceSupport = (value) => {
  clientCurrentBalanceColumnAvailable = value;

  if (typeof window === "undefined") {
    return;
  }

  try {
    window.sessionStorage.setItem(
      CLIENT_CURRENT_BALANCE_SUPPORT_KEY,
      value ? "true" : "false",
    );
  } catch {
    // Ignore storage access issues.
  }
};

const isMissingCurrentBalanceColumnError = (error) => {
  const details = [error?.message, error?.details, error?.hint, error?.code]
    .filter(Boolean)
    .join(" ")
    .toLowerCase();

  return details.includes("current_balance");
};

export const normalizeClientBalanceRow = (client) => {
  if (!client) return null;

  const normalizedBalance = Number(client.balance ?? client.current_balance ?? 0);
  const normalizedCurrentBalance = Number(
    client.current_balance ?? client.balance ?? 0,
  );

  return {
    ...client,
    balance: normalizedBalance,
    current_balance: normalizedCurrentBalance,
  };
};

// La definicion vive en balanceValues.js (modulo hoja, testeable sin browser).
// Se re-exporta para no tocar a quienes ya la importaban desde aca.
export { getClientBalanceValue } from "./balanceValues";
import { getClientBalanceValue } from "./balanceValues";

const CANCELLED_SALE_STATUSES = new Set([
  "anulada",
  "annulled",
  "cancelled",
  "cancelado",
]);
const NON_BALANCE_SALE_STATUSES = new Set(["pending"]);
const NON_BALANCE_INVOICE_STATUSES = new Set(["presupuesto"]);
const normalizeForgedSaleMovementTag = (value) =>
  String(value || "")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .replace(/[^a-z]/g, "");

const isForgedSaleMovement = (movement) => {
  const typeTag = normalizeForgedSaleMovementTag(movement?.type);
  if (typeTag.includes("ventapos") || typeTag.includes("ventaonline")) {
    return true;
  }

  if (!movement?.sale_id) return false;

  const observationTag = normalizeForgedSaleMovementTag(movement?.observations);
  return (
    observationTag.includes("ventapos") ||
    observationTag.includes("ventaonline")
  );
};

const normalizeSaleStatus = (value) => String(value || "").trim().toLowerCase();

const normalizeSaleText = (value) =>
  String(value || "")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .trim()
    .toLowerCase();

// Métodos de pago que implican cuenta corriente aunque la venta no tenga
// condición en días persistida (mismos tokens que storeOrderAccounting).
const CREDIT_PAYMENT_METHOD_TOKENS = [
  "cuenta corriente",
  "cta cte",
  "cta. cte",
  "fiado",
  "credito cliente",
  "credito comercial",
];

/**
 * ¿La venta fue a crédito (debe impactar la cuenta corriente)?
 * - Condición de venta en días > 0, o
 * - etiqueta de condición distinta de "contado", o
 * - método de pago tipo cuenta corriente/fiado.
 *
 * Las ventas de CONTADO a un cliente identificado NO generan deuda: se cobran
 * en el momento (el ingreso queda en movimientos_financieros/caja). Antes el
 * timeline las sumaba como deuda fantasma porque no tenían movimiento asociado.
 */
export const isCreditTermsSale = (sale) => {
  if (Number(sale?.payment_condition_days) > 0) return true;

  const conditionLabel = normalizeSaleText(sale?.payment_condition_label);
  if (conditionLabel && !conditionLabel.includes("contado")) return true;

  const paymentMethod = normalizeSaleText(sale?.payment_method);
  return CREDIT_PAYMENT_METHOD_TOKENS.some((token) =>
    paymentMethod.includes(token),
  );
};
const CLIENT_TIMELINE_SALES_BASE_SELECT =
  `id, client_id, total, status, active, invoice_status, payment_method, numero_factura, created_at, billing_source, ${SALES_WITH_STORE_ORDERS_EMBED}`;
const CLIENT_TIMELINE_SALES_SELECT = `${CLIENT_TIMELINE_SALES_BASE_SELECT}, ${SALE_PAYMENT_TERMS_SELECT_FIELDS}`;

export async function fetchSalesForClientTimeline({
  clientId,
  organizationId,
  supabaseClient = supabase,
}) {
  const buildQuery = (selectClause) => {
    let query = supabaseClient
      .from("sales")
      .select(selectClause)
      .eq("client_id", clientId);

    if (organizationId) {
      query = query.eq("organization_id", organizationId);
    }

    return query;
  };

  const support = getSalePaymentTermsColumnsSupport();

  if (support !== false) {
    const response = await buildQuery(CLIENT_TIMELINE_SALES_SELECT);

    if (!response.error) {
      setSalePaymentTermsColumnsSupport(true);
      return attachStoreOrdersToSales({
        sales: (response.data || []).map(normalizeSalePaymentTermsRow),
        organizationId,
        supabaseClient,
      });
    }

    if (!isMissingSalePaymentTermsColumnsError(response.error)) {
      return response;
    }

    setSalePaymentTermsColumnsSupport(false);
  }

  const fallbackResponse = await buildQuery(CLIENT_TIMELINE_SALES_BASE_SELECT);

  if (fallbackResponse.error) {
    return fallbackResponse;
  }

  const normalizedSales = (fallbackResponse.data || []).map(
    normalizeSalePaymentTermsRow,
  );
  return attachStoreOrdersToSales({
    sales: normalizedSales,
    organizationId,
    supabaseClient,
  });
}

export const isClientSaleBalanceRelevant = (sale) => {
  if (!sale?.id || !sale?.client_id) return false;
  if (sale.active === false) return false;

  const saleStatus = normalizeSaleStatus(sale.status);
  const invoiceStatus = normalizeSaleStatus(sale.invoice_status);
  const linkedStoreOrders = getSaleStoreOrders(sale);
  if (CANCELLED_SALE_STATUSES.has(saleStatus)) return false;
  if (CANCELLED_SALE_STATUSES.has(invoiceStatus)) return false;
  if (NON_BALANCE_INVOICE_STATUSES.has(invoiceStatus)) return false;

  if (isSaleAwaitingWebFulfillment(sale)) {
    return false;
  }

  if (linkedStoreOrders.length === 0 && NON_BALANCE_SALE_STATUSES.has(saleStatus)) {
    return false;
  }

  // Solo las ventas A CRÉDITO sin movimiento asociado representan deuda.
  // Las de contado se cobran en el acto: su ingreso vive en
  // movimientos_financieros/caja y no deben inflar la cuenta corriente.
  if (!isCreditTermsSale(sale)) return false;

  return Math.abs(Number(sale?.total || 0)) > 0.000001;
};

const buildClientTimelineKey = (entry) =>
  `${entry?.entryKind || "mov"}:${entry?.id || ""}`;

const getTimelineBaseBalance = (client) =>
  client?.initial_balance != null ? Number(client.initial_balance || 0) : 0;

export const buildClientAccountTimeline = ({
  client,
  movements = [],
  sales = [],
}) => {
  const salesById = new Map(
    (sales || []).filter((sale) => sale?.id).map((sale) => [sale.id, sale]),
  );
  const movementSaleIds = new Set(
    (movements || []).map((movement) => movement?.sale_id).filter(Boolean),
  );

  const accountRows = (movements || []).map((movement) => {
    const linkedSale = movement?.sale_id ? salesById.get(movement.sale_id) : null;
    const shouldUseSaleTimestamp =
      Boolean(linkedSale?.created_at) &&
      isForgedSaleMovement(movement);

    return {
      ...movement,
      created_at: shouldUseSaleTimestamp
        ? linkedSale.created_at
        : movement.created_at || movement.fecha || null,
      entryKind: "account_movement",
      timelineKey: buildClientTimelineKey({
        entryKind: "account_movement",
        id: movement.id,
      }),
    };
  });

  const saleRows = (sales || [])
    .filter(isClientSaleBalanceRelevant)
    .filter((sale) => !movementSaleIds.has(sale.id))
    .map((sale) => {
      const deliveredOrder = getDeliveredSaleStoreOrder(sale);
      const fulfillmentLabel = deliveredOrder
        ? deliveredOrder.delivery_type === "retiro"
          ? "Retirado"
          : "Entregado"
        : "";

      return {
        ...sale,
        sale_id: sale.id,
        entryKind: "sale",
        timelineKey: buildClientTimelineKey({ entryKind: "sale", id: sale.id }),
        type: sale.numero_factura ? "Venta facturada" : "Venta",
        amount: Number(sale.total || 0),
        observations: `Venta ${
          sale.numero_factura ? "facturada" : "sin factura"
        } - ${sale.payment_method || "Sin metodo de pago"}.${
          fulfillmentLabel ? ` ${fulfillmentLabel}.` : ""
        }${
          sale.payment_condition_label
            ? ` Condicion: ${sale.payment_condition_label}.`
            : ""
        }${sale.payment_due_date ? ` Vence: ${sale.payment_due_date}.` : ""}`,
      };
    });

  const ascendingRows = [...accountRows, ...saleRows].sort((left, right) => {
    const leftTime = new Date(left.created_at || left.fecha).getTime();
    const rightTime = new Date(right.created_at || right.fecha).getTime();
    if (leftTime !== rightTime) return leftTime - rightTime;
    return String(left.timelineKey || "").localeCompare(
      String(right.timelineKey || ""),
    );
  });

  // El consumidor final generico no tiene cuenta corriente: sus ventas son de
  // contado. Mostramos el historial de movimientos pero el saldo siempre es 0.
  const skipAccountBalance = isGenericConsumidorFinalClient(client);

  let runningBalance = getTimelineBaseBalance(client);

  const rowsWithBalance = ascendingRows.map((row) => {
    runningBalance += skipAccountBalance ? 0 : Number(row?.amount || 0);
    return {
      ...row,
      created_at: row.created_at || row.fecha || null,
      current_balance: runningBalance,
    };
  });

  return rowsWithBalance.sort(
    (left, right) =>
      new Date(right.created_at || right.fecha).getTime() -
      new Date(left.created_at || left.fecha).getTime(),
  );
};

const buildClientTimelineBalanceMap = ({
  clients = [],
  movements = [],
  sales = [],
}) => {
  const balancesByClient = new Map();
  const clientsWithTimeline = new Set();
  const movementSaleIds = new Set(
    (movements || []).map((movement) => movement?.sale_id).filter(Boolean),
  );
  // El consumidor final generico vende de contado: su cuenta corriente siempre
  // es 0, asi que sus movimientos y ventas no suman saldo.
  const skipAccountClientIds = new Set(
    (clients || [])
      .filter((client) => client?.id && isGenericConsumidorFinalClient(client))
      .map((client) => client.id),
  );

  (clients || []).forEach((client) => {
    if (!client?.id) return;
    balancesByClient.set(client.id, getTimelineBaseBalance(client));
  });

  (movements || []).forEach((movement) => {
    if (!movement?.client_id) return;
    if (skipAccountClientIds.has(movement.client_id)) return;
    clientsWithTimeline.add(movement.client_id);
    const currentBalance = Number(balancesByClient.get(movement.client_id) || 0);
    balancesByClient.set(
      movement.client_id,
      currentBalance + Number(movement?.amount || 0),
    );
  });

  (sales || []).filter(isClientSaleBalanceRelevant).forEach((sale) => {
    if (!sale?.client_id || movementSaleIds.has(sale.id)) return;
    if (skipAccountClientIds.has(sale.client_id)) return;
    clientsWithTimeline.add(sale.client_id);
    const currentBalance = Number(balancesByClient.get(sale.client_id) || 0);
    balancesByClient.set(
      sale.client_id,
      currentBalance + Number(sale?.total || 0),
    );
  });

  (clients || []).forEach((client) => {
    if (!client?.id || clientsWithTimeline.has(client.id)) return;
    // El consumidor final generico se mantiene en 0 (no usa el balance persistido,
    // que puede arrastrar debitos historicos de ventas de contado).
    if (skipAccountClientIds.has(client.id)) {
      balancesByClient.set(client.id, 0);
      return;
    }
    balancesByClient.set(client.id, Number(client?.balance || 0));
  });

  return balancesByClient;
};

const applyComputedBalanceToClient = (client, nextBalance) => {
  if (!client) return null;

  const normalizedBalance = Number(nextBalance || 0);
  return {
    ...client,
    balance: normalizedBalance,
    current_balance: normalizedBalance,
  };
};

export async function fetchClientBalanceSnapshot({
  clientId,
  supabaseClient = supabase,
}) {
  if (!clientId) {
    return { data: null, error: null };
  }

  const response = await supabaseClient
    .from("clients")
    .select("id, balance")
    .eq("id", clientId)
    .maybeSingle();

  if (response.error) {
    return { data: null, error: response.error };
  }

  if (response.data) {
    const hasCurrentBalanceColumn = Object.prototype.hasOwnProperty.call(
      response.data,
      "current_balance",
    );
    writeClientCurrentBalanceSupport(hasCurrentBalanceColumn);
  }

  return {
    data: normalizeClientBalanceRow(response.data),
    error: null,
  };
}

export async function fetchClientTimelineBalanceSnapshot({
  clientId,
  organizationId,
  ignoredSaleIds = [],
  supabaseClient = supabase,
}) {
  if (!clientId) {
    return { data: null, error: null };
  }

  let clientQuery = supabaseClient
    .from("clients")
    .select("*")
    .eq("id", clientId);

  let movementsQuery = supabaseClient
    .from("client_movements")
    .select(
      "id, client_id, amount, sale_id, type, current_balance, observations, created_at",
    )
    .eq("client_id", clientId);

  if (organizationId) {
    clientQuery = clientQuery.eq("organization_id", organizationId);
    movementsQuery = movementsQuery.eq("organization_id", organizationId);
  }

  const [clientResponse, movementsResponse, salesResponse] = await Promise.all([
    clientQuery.maybeSingle(),
    movementsQuery,
    fetchSalesForClientTimeline({
      clientId,
      organizationId,
      supabaseClient,
    }),
  ]);

  if (clientResponse.error) {
    return { data: null, error: clientResponse.error };
  }

  if (clientResponse.data) {
    const hasCurrentBalanceColumn = Object.prototype.hasOwnProperty.call(
      clientResponse.data,
      "current_balance",
    );
    writeClientCurrentBalanceSupport(hasCurrentBalanceColumn);
  }

  if (movementsResponse.error) {
    return { data: null, error: movementsResponse.error };
  }

  if (salesResponse.error) {
    return { data: null, error: salesResponse.error };
  }

  const ignoredSaleIdsSet = new Set(ignoredSaleIds);
  const timeline = buildClientAccountTimeline({
    client: clientResponse.data,
    movements: movementsResponse.data || [],
    sales: (salesResponse.data || []).filter(
      (sale) => !ignoredSaleIdsSet.has(sale?.id),
    ),
  });
  const fallbackBalance = Number(
    clientResponse.data?.initial_balance ??
      clientResponse.data?.current_balance ??
      clientResponse.data?.balance ??
      0,
  );
  const nextBalance = Number(timeline[0]?.current_balance ?? fallbackBalance);

  return {
    data: applyComputedBalanceToClient(clientResponse.data, nextBalance),
    error: null,
  };
}

export async function updateClientBalance({
  clientId,
  nextBalance,
  supabaseClient = supabase,
}) {
  if (!clientId) {
    return { data: null, error: null };
  }

  const normalizedBalance = Number(nextBalance || 0);

  const currentSupport = readClientCurrentBalanceSupport();

  if (currentSupport !== false) {
    const response = await supabaseClient
      .from("clients")
      .update({
        balance: normalizedBalance,
        current_balance: normalizedBalance,
      })
      .eq("id", clientId);

    if (!response.error) {
      writeClientCurrentBalanceSupport(true);
      return {
        data: {
          id: clientId,
          balance: normalizedBalance,
          current_balance: normalizedBalance,
        },
        error: null,
      };
    }

    if (!isMissingCurrentBalanceColumnError(response.error)) {
      return { data: null, error: response.error };
    }

    writeClientCurrentBalanceSupport(false);
  }

  const fallbackResponse = await supabaseClient
    .from("clients")
    .update({
      balance: normalizedBalance,
    })
    .eq("id", clientId);

  if (fallbackResponse.error) {
    return { data: null, error: fallbackResponse.error };
  }

  return {
    data: {
      id: clientId,
      balance: normalizedBalance,
      current_balance: normalizedBalance,
    },
    error: null,
  };
}

export async function fetchActiveOrganizationClientsWithBalances({
  organizationId,
  supabaseClient = supabase,
  // statusFilter: 'active' (default) | 'inactive' | 'all'
  // includeInactive: shortcut booleano para retro-compatibilidad.
  statusFilter = "active",
  includeInactive = false,
}) {
  if (!organizationId) {
    return { data: [], error: null };
  }

  const resolvedStatusFilter = includeInactive ? "all" : statusFilter;

  let clientsQuery = supabaseClient
    .from("clients")
    .select("*")
    .eq("organization_id", organizationId);
  if (resolvedStatusFilter === "active") {
    clientsQuery = clientsQuery.eq("is_active", true);
  } else if (resolvedStatusFilter === "inactive") {
    clientsQuery = clientsQuery.eq("is_active", false);
  }
  clientsQuery = clientsQuery.order("full_name");

  // payment_method + condición de venta son necesarios para distinguir ventas
  // a crédito (impactan saldo) de ventas de contado (no impactan). Las columnas
  // de condición pueden no existir en esquemas viejos → mismo fallback que
  // fetchSalesForClientTimeline.
  const orgSalesBaseSelect = `id, client_id, total, status, active, invoice_status, payment_method, billing_source, ${SALES_WITH_STORE_ORDERS_EMBED}`;
  const orgSalesSelectWithTerms = `${orgSalesBaseSelect}, ${SALE_PAYMENT_TERMS_SELECT_FIELDS}`;

  const buildOrgSalesQuery = (selectClause) =>
    supabaseClient
      .from("sales")
      .select(selectClause)
      .eq("organization_id", organizationId);

  const fetchOrgSales = async () => {
    if (getSalePaymentTermsColumnsSupport() !== false) {
      const response = await buildOrgSalesQuery(orgSalesSelectWithTerms);

      if (!response.error) {
        setSalePaymentTermsColumnsSupport(true);
        return response;
      }

      if (!isMissingSalePaymentTermsColumnsError(response.error)) {
        return response;
      }

      setSalePaymentTermsColumnsSupport(false);
    }

    return buildOrgSalesQuery(orgSalesBaseSelect);
  };

  const [clientsResponse, movementsResponse, salesResponse] = await Promise.all([
    clientsQuery,
    supabaseClient
      .from("client_movements")
      .select("id, client_id, amount, sale_id")
      .eq("organization_id", organizationId),
    fetchOrgSales(),
  ]);

  if (clientsResponse.error) {
    return { data: [], error: clientsResponse.error };
  }

  const firstClientRow = (clientsResponse.data || []).find(Boolean) || null;
  if (firstClientRow) {
    const hasCurrentBalanceColumn = Object.prototype.hasOwnProperty.call(
      firstClientRow,
      "current_balance",
    );
    writeClientCurrentBalanceSupport(hasCurrentBalanceColumn);
  }

  if (movementsResponse.error) {
    return { data: [], error: movementsResponse.error };
  }

  if (salesResponse.error) {
    return { data: [], error: salesResponse.error };
  }

  const attachedSalesRes = await attachStoreOrdersToSales({
    sales: (salesResponse.data || []).map(normalizeSalePaymentTermsRow),
    organizationId,
    supabaseClient,
  });
  if (attachedSalesRes.error) {
    return { data: [], error: attachedSalesRes.error };
  }

  const clients = clientsResponse.data || [];
  const balanceMap = buildClientTimelineBalanceMap({
    clients,
    movements: movementsResponse.data || [],
    sales: attachedSalesRes.data || [],
  });

  return {
    data: clients.map((client) =>
      applyComputedBalanceToClient(
        client,
        balanceMap.get(client.id) ?? Number(client?.balance || 0),
      ),
    ),
    error: null,
  };
}
